/*
 * Gameplay Offering effects and strict upgrades.
 *
 * The Wiki snapshot intentionally stays compact and many Offerings expose only
 * semantic tags there. These profiles keep the exact gameplay magnitude needed
 * by the optimiser and by the in-app information sheet.
 */

export const OFFERING_GAMEPLAY_PROFILES = {
  annotated_blueprint: {
    effect: 'Increases the likelihood of the Hatch spawning at the Killer Shack by +100%, if available.',
    plans: ['endgame_survival'],
    signals: ['endgame'],
    family: 'hatch_shack',
    rank: 1
  },
  vigo_s_blueprint: {
    effect: "Increases the likelihood of the Hatch spawning at the Map's main Landmark building by +100%, if available.",
    plans: ['endgame_survival'],
    signals: ['endgame'],
    family: 'hatch_landmark',
    rank: 1
  },
  bloodied_blueprint: {
    effect: 'Increases the likelihood of the Basement spawning at the Killer Shack by +100% and reveals its Hook Auras for 20 seconds.',
    plans: [],
    signals: ['hook'],
    family: 'basement_shack',
    rank: 1
  },
  torn_blueprint: {
    effect: "Increases the likelihood of the Basement spawning at the Map's main Landmark building by +100% and reveals its Hook Auras for 20 seconds.",
    plans: [],
    signals: ['hook'],
    family: 'basement_landmark',
    rank: 1
  },
  clear_reagent: {
    effect: 'Slightly decreases the density of the Dark Mist.',
    plans: ['aura_information'],
    signals: ['information'],
    family: 'fog_clear',
    rank: 1
  },
  faint_reagent: {
    effect: 'Slightly increases the density of the Dark Mist.',
    plans: ['stealth_evasion', 'chase_escape'],
    signals: ['stealth'],
    family: 'fog_dense',
    rank: 1
  },
  hazy_reagent: {
    effect: 'Moderately increases the density of the Dark Mist.',
    plans: ['stealth_evasion', 'chase_escape'],
    signals: ['stealth'],
    family: 'fog_dense',
    rank: 2
  },
  murky_reagent: {
    effect: 'Considerably increases the density of the Dark Mist.',
    plans: ['stealth_evasion', 'chase_escape'],
    signals: ['stealth'],
    family: 'fog_dense',
    rank: 3
  },
  tarnished_coin: {
    effect: 'Increases the number of Chests in the environment by +1.',
    plans: ['item_economy'],
    signals: ['loot', 'item'],
    family: 'survivor_chests',
    rank: 1
  },
  shiny_coin: {
    effect: 'Increases the number of Chests in the environment by +2.',
    plans: ['item_economy'],
    signals: ['loot', 'item'],
    family: 'survivor_chests',
    rank: 2
  },
  scratched_coin: {
    effect: 'Decreases the number of Chests in the environment by -1.',
    plans: [],
    signals: ['loot'],
    family: 'killer_chests',
    rank: 1
  },
  cut_coin: {
    effect: 'Decreases the number of Chests in the environment by -2.',
    plans: [],
    signals: ['loot'],
    family: 'killer_chests',
    rank: 2
  },
  chalk_pouch: {
    effect: 'Increases your personal Luck by +1%.',
    plans: [],
    signals: ['hook'],
    family: 'personal_luck',
    rank: 1
  },
  cream_chalk_pouch: {
    effect: 'Increases your personal Luck by +2%.',
    plans: [],
    signals: ['hook'],
    family: 'personal_luck',
    rank: 2
  },
  ivory_chalk_pouch: {
    effect: 'Increases your personal Luck by +3%.',
    plans: [],
    signals: ['hook'],
    family: 'personal_luck',
    rank: 3
  },
  mouldy_oak: {
    effect: 'Slightly decreases the minimum distance between Hooks.',
    plans: [],
    signals: ['hook'],
    family: 'killer_hook_spacing',
    rank: 1
  },
  rotten_oak: {
    effect: 'Moderately decreases the minimum distance between Hooks.',
    plans: [],
    signals: ['hook'],
    family: 'killer_hook_spacing',
    rank: 2
  },
  putrid_oak: {
    effect: 'Considerably decreases the minimum distance between Hooks.',
    plans: [],
    signals: ['hook'],
    family: 'killer_hook_spacing',
    rank: 3
  },
  petrified_oak: {
    effect: 'Slightly increases the minimum distance between Hooks.',
    plans: ['sabotage_save', 'protection_survival'],
    signals: ['hook', 'sabotage'],
    family: 'survivor_hook_spacing',
    rank: 1
  },
  ivory_memento_mori: {
    effect: 'Allows the Killer to kill one Survivor who has reached the second Hook Stage by their own hand.',
    plans: [],
    signals: ['hook'],
    family: 'mori',
    rank: 1
  },
  ebony_memento_mori: {
    effect: 'Allows the Killer to kill all Survivors who have reached the second Hook Stage by their own hand.',
    plans: [],
    signals: ['hook'],
    family: 'mori',
    rank: 2
  },
  shroud_of_union: {
    effect: 'Causes all Survivors to start the Trial together.',
    plans: ['linked_teamwork', 'healing_support', 'generator_pressure', 'rescue_teamplay'],
    signals: ['generator', 'healing', 'hook'],
    family: 'survivor_spawn_together',
    rank: 1
  },
  vigo_s_shroud: {
    effect: 'Causes you to start the Trial as far away from the Killer as possible.',
    plans: ['stealth_evasion', 'chase_escape'],
    signals: ['stealth'],
    family: 'survivor_spawn_safe',
    rank: 1
  },
  shroud_of_vanishing: {
    effect: 'Causes all Survivors to start the Trial separated from one another.',
    plans: [],
    signals: ['generator'],
    family: 'killer_spawn_separated',
    rank: 1
  }
}

export function offeringGameplayProfile (offering) {
  return offering ? OFFERING_GAMEPLAY_PROFILES[offering.id] || null : null
}

export function isStrictlyDominatedOffering (offering, pool) {
  const profile = offeringGameplayProfile(offering)
  if (!profile || !profile.family) return false
  return (pool || []).some(candidate => {
    const candidateProfile = offeringGameplayProfile(candidate)
    return candidateProfile &&
      candidateProfile.family === profile.family &&
      candidateProfile.rank > profile.rank
  })
}
